import { Button, Popover, PopoverContent, PopoverTrigger } from "@ondit/ui";
import { Bell } from "@ondit/ui/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { api, type NotificationItem } from "~/core/api";
import { relative } from "~/core/format";
import { useI18n } from "~/core/i18n";
import type { I18n } from "~/core/i18n/context";
import { keys, read } from "~/core/query";
import { Empty } from "~/core/ui/states";

/** What a row says. A workflow's message is its author's words; the rest are ours around the brand and the thing. */
export function describe(t: I18n["t"], item: NotificationItem): { heading: string; detail: string } {
	const { payload } = item;
	if (item.kind === "workflow_message") {
		return { heading: payload.title ?? "", detail: [payload.body, payload.subject].filter(Boolean).join(" · ") };
	}
	const task = payload.task ? t(`jobs.${payload.task}`) : null;
	return {
		heading: t(`notifications.kind.${item.kind}`),
		detail: [payload.brand, payload.subject || task].filter(Boolean).join(" · "),
	};
}

/** The shell's bell: this account's notifications for the person reading, whichever module they are in. */
export function NotificationBell({ orgId, settingsHref }: { orgId: string; settingsHref: string }) {
	const { t, locale } = useI18n();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [open, setOpen] = useState(false);
	const queryKey = [...keys.org(orgId), "notifications"];
	// The org stream invalidates this on every new row; the interval only covers a stream that dropped.
	const feed = useQuery({
		queryKey,
		queryFn: () => api.notifications(orgId),
		...read,
		refetchInterval: 60_000,
		refetchOnWindowFocus: true,
	});
	const markRead = useMutation({
		mutationFn: (ids?: string[]) => api.readNotifications(orgId, ids),
		onSuccess: () => queryClient.invalidateQueries({ queryKey }),
	});
	const unread = feed.data?.unread ?? 0;
	const items = feed.data?.items ?? [];

	function follow(item: NotificationItem) {
		if (!item.read_at) markRead.mutate([item.id]);
		setOpen(false);
		if (item.link) navigate(item.link);
	}

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="ghost"
					size="icon"
					className="notify-bell size-[var(--control-h-sm)]"
					aria-label={unread ? t("notifications.open", { n: String(unread) }) : t("notifications.title")}
				>
					<Bell className="size-[var(--s-8)]" />
					{unread ? <span className="notify-dot" /> : null}
				</Button>
			</PopoverTrigger>
			<PopoverContent
				className="notify-panel bg-[var(--bg)] shadow-xs dark:bg-[var(--bg)]"
				border
				side="bottom"
				align="start"
				sideOffset={6}
				maxWidth={360}
			>
				<div className="notify-head">
					<strong>{t("notifications.title")}</strong>
					{unread ? (
						<Button variant="ghost" size="sm" disabled={markRead.isPending} onClick={() => markRead.mutate(undefined)}>
							{t("notifications.markAllRead")}
						</Button>
					) : null}
				</div>
				{items.length === 0 ? (
					<Empty title={t("notifications.empty")} body={t("notifications.emptyBody")} />
				) : (
					<ul className="notify-list">
						{items.map((item) => {
							const { heading, detail } = describe(t, item);
							return (
								<li key={item.id}>
									<button
										type="button"
										className="notify-item"
										data-unread={!item.read_at || undefined}
										onClick={() => follow(item)}
									>
										<span className="notify-heading">{heading}</span>
										{detail ? <span className="notify-detail">{detail}</span> : null}
										<time className="notify-time" dateTime={item.created_at}>
											{relative(locale, item.created_at)}
										</time>
									</button>
								</li>
							);
						})}
					</ul>
				)}
				<div className="notify-foot">
					<Link to={settingsHref} onClick={() => setOpen(false)}>
						{t("notifications.settings")}
					</Link>
				</div>
			</PopoverContent>
		</Popover>
	);
}
